import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BookMarked, Trash2, ShoppingCart, Headphones, Library as LibraryIcon, ExternalLink, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { READING } from "@/constants/testIds/app";

const linkIconOf = { read: ExternalLink, buy: ShoppingCart, audio: Headphones, library: LibraryIcon, listen: Headphones, watch: ExternalLink };
const READING_STATUSES = ["to-read", "to-buy"];

export default function ReadingList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/library");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const readingItems = useMemo(
    () =>
      items.filter(
        (e) => e.item?.type === "book" && READING_STATUSES.includes(e.status)
      ),
    [items]
  );
  const filtered = useMemo(
    () => (statusFilter === "all" ? readingItems : readingItems.filter((e) => e.status === statusFilter)),
    [readingItems, statusFilter]
  );

  const counts = useMemo(() => {
    const c = { all: readingItems.length };
    READING_STATUSES.forEach((s) => (c[s] = readingItems.filter((e) => e.status === s).length));
    return c;
  }, [readingItems]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/library/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      if (!READING_STATUSES.includes(status)) {
        toast.success("Moved to your library");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/library/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid={READING.container} className="max-w-5xl">
      <div className="label-caps mb-4">My reading list</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C2520] tracking-tight">
        Books saved for later.
      </h1>
      <p className="mt-5 text-[#2C2520]/70 leading-relaxed max-w-xl">
        A quiet holding place for books you want to read soon, or buy when the moment feels right.
      </p>

      {loading ? (
        <p className="mt-10 text-[#2C2520]/60 italic font-serif-display">Turning to the next page…</p>
      ) : readingItems.length === 0 ? (
        <div
          data-testid={READING.empty}
          className="mt-14 border border-dashed border-[#E8E3D5] rounded-[12px] p-12 text-center bg-[#F5F2EA]/40"
        >
          <BookMarked className="w-8 h-8 text-[#4A5D4E]/50 mx-auto" strokeWidth={1.5} />
          <p className="mt-4 font-serif-display italic text-lg text-[#2C2520]/70">
            Nothing here yet.
          </p>
          <p className="text-sm text-[#2C2520]/55 mt-1">
            On any book recommendation, tap "For later" to send it here.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-10">
            <div className="label-caps mb-2">Filter</div>
            <div className="flex flex-wrap gap-2">
              <FilterChip
                active={statusFilter === "all"}
                onClick={() => setStatusFilter("all")}
                testid="reading-list-filter-all"
              >
                All <span className="opacity-60 ml-1">·{counts.all}</span>
              </FilterChip>
              <FilterChip
                active={statusFilter === "to-read"}
                onClick={() => setStatusFilter("to-read")}
                testid="reading-list-filter-to-read"
              >
                To read <span className="opacity-60 ml-1">·{counts["to-read"]}</span>
              </FilterChip>
              <FilterChip
                active={statusFilter === "to-buy"}
                onClick={() => setStatusFilter("to-buy")}
                testid="reading-list-filter-to-buy"
              >
                To buy <span className="opacity-60 ml-1">·{counts["to-buy"]}</span>
              </FilterChip>
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="mt-10 text-[#2C2520]/60 italic font-serif-display" data-testid="reading-list-empty-filtered">
              Nothing matches this filter.
            </p>
          ) : (
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((entry) => {
                const it = entry.item;
                return (
                  <div
                    key={entry.id}
                    data-testid={READING.item(entry.id)}
                    className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] overflow-hidden flex flex-col hover:shadow-[0_8px_24px_rgba(44,37,32,0.08)] transition-shadow duration-300"
                  >
                    {it.cover_url ? (
                      <div className="aspect-[3/4] bg-[#EDE8DB] overflow-hidden">
                        <img
                          src={it.cover_url}
                          alt={it.title}
                          className="w-full h-full object-cover"
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      </div>
                    ) : (
                      <div className="aspect-[3/4] bg-[#EDE8DB] flex items-center justify-center">
                        <BookMarked className="w-10 h-10 text-[#4A5D4E]/60" strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="p-5 flex-1 flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="label-caps">Book</div>
                        <span
                          className={
                            "text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full " +
                            (entry.status === "to-buy"
                              ? "bg-[#4A5D4E] text-white"
                              : "bg-[#EDE8DB] text-[#4A5D4E]")
                          }
                        >
                          {entry.status === "to-buy" ? "To buy" : "To read"}
                        </span>
                      </div>
                      <h3 className="font-serif-display text-lg text-[#2C2520] leading-snug">{it.title}</h3>
                      <p className="text-xs text-[#2C2520]/65">{it.author}</p>

                      {it.access_links?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {it.access_links.map((lnk, li) => {
                            const LIcon = linkIconOf[lnk.kind] || ExternalLink;
                            const primary = lnk.kind === "buy" && entry.status === "to-buy" && li === 1;
                            return (
                              <a
                                key={li}
                                href={lnk.url}
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid={`reading-list-access-${entry.id}-${lnk.kind}`}
                                className={
                                  "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] transition-colors " +
                                  (primary
                                    ? "bg-[#4A5D4E] text-white hover:bg-[#3D4D40]"
                                    : "bg-[#FDFBF7] border border-[#E8E3D5] text-[#2C2520] hover:bg-[#EDE8DB]")
                                }
                              >
                                <LIcon className="w-3 h-3" strokeWidth={1.5} />
                                {lnk.label}
                              </a>
                            );
                          })}
                        </div>
                      )}

                      <div className="mt-auto pt-3 flex items-center gap-2 flex-wrap">
                        <select
                          data-testid={READING.statusSelect(entry.id)}
                          value={entry.status}
                          onChange={(e) => updateStatus(entry.id, e.target.value)}
                          className="text-xs px-2 py-1.5 rounded-[8px] bg-[#FDFBF7] border border-[#E8E3D5] text-[#2C2520]"
                        >
                          <option value="to-read">To read</option>
                          <option value="to-buy">To buy</option>
                        </select>
                        <button
                          data-testid={READING.moveToLibrary(entry.id)}
                          onClick={() => updateStatus(entry.id, "reading")}
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-[8px] border border-[#E8E3D5] text-xs text-[#2C2520] hover:bg-[#EDE8DB]"
                          title="Start reading"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          Start
                        </button>
                        <button
                          data-testid={READING.deleteBtn(entry.id)}
                          onClick={() => remove(entry.id)}
                          className="ml-auto p-1.5 rounded-[8px] text-[#2C2520]/50 hover:text-[#8B3A3A] hover:bg-[#F5E9E6]"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={
        "px-3 py-1.5 rounded-full text-xs capitalize transition-colors border " +
        (active
          ? "bg-[#4A5D4E] text-white border-[#4A5D4E]"
          : "bg-[#F5F2EA] text-[#2C2520] border-[#E8E3D5] hover:bg-[#EDE8DB]")
      }
    >
      {children}
    </button>
  );
}
